import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormCreateReportSchema } from 'utils/validate';
import { useFlash } from 'contexts/flash';
import { createAssetIssue } from 'services/locations';
import { appendDynamicOptionForPayload, appendDynamicSingleForPayload } from 'utils/convertData';
export type FormDetailReport = {
  description?: string;
  equipmentId?: string;
  status?: string;
  actionBy?: string;
  actionByDate?: string;
  note?: string | null;
  name: string;
  uploadedFiles?: File[];
  actionByName?: string;
} & {
  [key: string]: unknown;
};

export const useInjection = () => {
  const { setFlash } = useFlash();
  const {
    handleSubmit,
    register,
    control,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<FormDetailReport>({
    resolver: yupResolver(FormCreateReportSchema)
  });
  const [search, setSearch] = useState<string>('');

  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const photos = watch('uploadedFiles');

  const [searchParams] = useSearchParams(window.location.search);

  const paths = ['Locations', searchParams.get('room-name'), searchParams.get('equipment-id')];

  useEffect(() => {
    setValue('equipmentId', searchParams.get('equipment-id') || '');
  }, [searchParams, setValue]);

  const handleDeleteImg = useCallback(
    (index: number) => {
      const updatedPhotos = [...(photos || [])];
      updatedPhotos.splice(index, 1);
      setValue('uploadedFiles', updatedPhotos);
    },
    [photos, setValue]
  );

  const handleSubmitForm = useCallback(
    async (valueForm: FormDetailReport) => {
      try {
        await createAssetIssue({
          id: Number(searchParams.get('asset-id')),
          submitted: Number(searchParams.get('submitted-id')),
          payload: {
            actionBy: valueForm.actionBy === 'OTHER' ? valueForm.actionByName : valueForm.actionBy,
            name: valueForm.name,
            actionByDate: valueForm?.actionByDate || '',
            equipmentId: valueForm?.equipmentId || '',
            description: valueForm?.description || '',
            note: valueForm.note,
            uploadedFiles: valueForm.uploadedFiles,
            options: appendDynamicOptionForPayload(valueForm),
            singleFields: appendDynamicSingleForPayload(valueForm)
          }
        });
        setFlash({ message: 'Created a Report successful!', type: 'success' });
        reset({
          equipmentId: valueForm.equipmentId,
          description: '',
          name: '',
          actionByDate: '',
          actionBy: '',
          actionByName: ''
        });
      } catch (error) {
        setFlash({ message: 'Failed to create a Report', type: 'error' });
        console.error('Error creating report:', error);
      }
    },
    [reset, searchParams, setFlash]
  );

  const { asyncCallback: asyncHandleSubmitForm } = useAsyncCallback(handleSubmitForm, [
    handleSubmitForm
  ]);

  return {
    search,
    photos,
    paths,
    setSearch,
    fileInputRef,
    handleDeleteImg,
    isDragging,
    setIsDragging,
    handleSubmit: handleSubmit(asyncHandleSubmitForm),
    register,
    reset,
    errors,
    control,
    setValue,
    watch
  };
};
